import { combineReducers } from 'redux'
import { browserHistory } from 'utils/navigation'
import moment from 'moment'
import request, { generateNotyMessage } from 'utils/request'
import {
  BLOB_TYPE_MAP,
  DATE_FORMAT_MAP,
  MODAL_BUTTONS_MAP,
  MODAL_CLASSES_MAP,
  MODAL_CONTENT_MAP,
  NOT_APPLICABLE_PLACEHOLDER,
  PARTICIPANT_CONSENT_STATUS_MAP,
  REDIRECT_DELAY,
  STATUS_OPTIONS,
  DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION,
  INSTRUMENT_TYPE_MAP,
  COMBINED_INSTRUMENT_TIMELINE_MAP,
  PARTICIPANT_KEYS,
  REGEX_PARTS
} from 'utils/constants'
import { downloadBlob, fileTypeRegex } from 'utils/misc'
import { today, getFormattedUTCTimestamp } from 'utils/time'
import { objMapToList } from 'utils/object'
import { modalActions } from 'store/modal'
import { loadingActions } from 'store/loader'
import { sendingActions } from 'store/sender'
import documents, { documentsActions } from 'store/documents'
import { actions as notyActions } from 'layouts/ErrorBox'
import { getParticipantErrors } from '../../../utils/participantValidation'
import {
  _fetchPayments,
  _fetchTreasureChestPayoutHistory,
  getFormattedPayments,
} from '../../../../Payments/modules/Payments'
import { actions as ptpActions } from '../../ParticipantsPage/modules/Participants'
import {
  _fetchVisitSchedules,
  setScheduleVisitTemplates,
} from '../../../../VisitSchedules/routes/VisitSchedulesPage/modules/VisitSchedules'
import { setAdhocVisit } from '../../../../VisitSchedules/routes/VisitScheduleNav/routes/ParticipantVisitPage/modules/ParticipantVisit'
import { patchParticipantScheduleVisit } from '../../../../VisitSchedules/utils/participantScheduleVisitApi'
import { getStudyVersionID } from '../../../../../../../../../utils/studyConfigUtils'

const DEFAULT_PTP_DATA_FIELDS_CONFIG = {
  mandatory: [
    {
      display_label: 'Preferred Name',
      type: 'text',
      metadata: {
        internal_key: 'fname',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Last Name',
      type: 'text',
      metadata: {
        internal_key: 'lname',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Email',
      type: 'email',
      metadata: {
        internal_key: 'email',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Sites',
      type: 'number',
      metadata: {
        internal_key: 'site_id',
        ui_type: 'custom_dropdown',
        custom_validations: '',
        is_custom: false,
        default_value: null,
        can_update: true,
      },
    },
  ],
  optional: [
    {
      display_label: 'Pre-Screen ID',
      type: 'text',
      metadata: {
        internal_key: 'pre_screen_id',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Subject ID',
      type: 'text',
      metadata: {
        internal_key: 'subject_id',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Caregiver',
      type: 'checkbox',
      metadata: {
        internal_key: 'is_caregiver',
        custom_validations: '',
        is_custom: false,
        default_value: false,
        can_update: false,
      },
    },
    {
      display_label: 'Locale',
      type: 'text',
      metadata: {
        internal_key: 'default_locale',
        custom_validations: '',
        is_custom: false,
        default_value: 'en_US',
        can_update: true,
      },
    },
    {
      display_label: 'Sex',
      type: 'text',
      metadata: {
        internal_key: 'gender',
        custom_validations: '',
        is_custom: false,
        default_value: 'n',
        can_update: true,
      },
    },
    {
      display_label: 'Date of Birth',
      type: 'date',
      metadata: {
        internal_key: 'date_of_birth',
        custom_validations: '',
        is_custom: false,
        default_value: '1980-01-01',
        can_update: true,
      },
    },
    {
      display_label: 'Phone',
      type: 'tel',
      metadata: {
        internal_key: 'phone',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
  ],
  disabled: [],
}

//
// Actions
//

const UPDATE_FIELD = 'UPDATE_FIELD'
const INITIALIZE_CREATE_PTP = 'INITIALIZE_CREATE_PTP'
const SET_PARTICIPANT = 'SET_PARTICIPANT'
const SET_V2_PARTICIPANT = 'SET_V2_PARTICIPANT'
export const RESET_PARTICIPANT = 'RESET_PARTICIPANT'
const INITIALIZE_CHECKED_COHORTS = 'INITIALIZE_CHECKED_COHORTS'
const TOGGLE_COHORT = 'TOGGLE_COHORT'
const CLEAR_COHORTS = 'CLEAR_COHORTS'
const SET_PTP_PAYMENTS = 'SET_PTP_PAYMENTS'
const SET_LOCALES = 'SET_LOCALES'
const SET_FILES = 'SET_FILES'
const SET_PARTICIPANT_INSTRUMENTS = 'SET_PARTICIPANT_INSTRUMENTS'
const SET_PARTICIPANT_ANNOUNCEMENTS = 'SET_PARTICIPANT_ANNOUNCEMENTS'
const SET_PARTICIPANT_HISTORY = 'SET_PARTICIPANT_HISTORY'
const SET_V2_PARTICIPANT_INSTRUMENTS = 'SET_V2_PARTICIPANT_INSTRUMENTS'
const SET_V2_PARTICIPANT_ANNOUNCEMENTS = 'SET_V2_PARTICIPANT_ANNOUNCEMENTS'
const SET_INSTRUMENT_TIMELINE = 'SET_INSTRUMENT_TIMELINE'
const SET_V2_INSTRUMENT_TIMELINE = 'SET_V2_INSTRUMENT_TIMELINE'
const SET_DIARY_TIMELINE = 'SET_DIARY_TIMELINE'
const SET_ANNOUNCEMENT_TIMELINE = 'SET_ANNOUNCEMENT_TIMELINE'
const SET_V2_ANNOUNCEMENT_TIMELINE = 'SET_V2_ANNOUNCEMENT_TIMELINE'
const RESET_INSTRUMENT_TIMELINE = 'RESET_INSTRUMENT_TIMELINE'
const RESET_DIARY_TIMELINE = 'RESET_DIARY_TIMELINE'
const RESET_ANNOUNCEMENT_TIMELINE = 'RESET_ANNOUNCEMENT_TIMELINE'
const SET_INSTRUMENT_SUBMISSION = 'SET_INSTRUMENT_SUBMISSION'
const RESET_INSTRUMENT_SUBMISSION = 'RESET_INSTRUMENT_SUBMISSION'
const SET_INSTRUMENT_SUBMISSION_ERROR = 'SET_INSTRUMENT_SUBMISSION_ERROR'
export const SET_PARTICIPANT_VISITS = 'SET_PARTICIPANT_VISITS'
export const CLEAR_PARTICIPANT_VISITS = 'CLEAR_PARTICIPANT_VISITS'
export const SET_VISIT_TEMPLATES = 'SET_VISIT_TEMPLATES'
const SET_SUBJECT_ID_STATUS = 'SET_SUBJECT_ID_STATUS'
const RESET_SUBJECT_ID_STATUS = 'RESET_SUBJECT_ID_STATUS'
const SET_PARTICIPANT_VISIT_CYCLES = 'SET_PARTICIPANT_VISIT_CYCLES'
const CLEAR_PARTICIPANT_VISIT_CYCLES = 'CLEAR_PARTICIPANT_VISIT_CYCLES'
// Visit instruments (linked to a participant visit)
const SET_VISIT_INSTRUMENTS = 'SET_VISIT_INSTRUMENTS'
const SET_VISIT_INSTRUMENTS_LOADING = 'SET_VISIT_INSTRUMENTS_LOADING'

//
// Action Creators
//

export function toggleCohort(id) {
  return {
    type: TOGGLE_COHORT,
    id,
  }
}

export function clearCohorts() {
  return {
    type: CLEAR_COHORTS,
  }
}

const resetParticipant = () => {
  return {
    type: RESET_PARTICIPANT,
  }
}

const clearPtpVisits = () => {
  return {
    type: CLEAR_PARTICIPANT_VISITS,
  }
}

const clearPtpVisitCycles = () => {
  return {
    type: CLEAR_PARTICIPANT_VISIT_CYCLES,
  }
}

const setSubjectIdStatus = subjectIdStatus => {
  return {
    type: SET_SUBJECT_ID_STATUS,
    subjectIdStatus,
  }
}

const resetSubjectIdStatus = () => {
  return {
    type: RESET_SUBJECT_ID_STATUS,
  }
}

export function saveToDatabase({
  studyID,
  siteID,
  participant,
  checkedCohorts: _checkedCohorts,
  participantID,
  isPatch = false,
  sitesArr,
  pDataFields,
  isNotLeaving,
  participantField,
  redirect = null,
  hideSuccessMessage = false,
  armId,
  useFormSitesForPatch = false,
}) {
  let currentSite
  return (dispatch, getState) => {
    const { drawer } = getState()
    const studyVersionID = getStudyVersionID(getState()?.location)
    const hasStudyPromotion = !!getState()?.study?.currentStudy?.config?.study_promotion

    const selectedTracks = Object.keys(_checkedCohorts)
    if (
      !validateParticipant({
        participant,
        dispatch,
        sitesArr,
        pDataFields,
        selectedTracks: selectedTracks.length ? selectedTracks : null,
        isPatch,
      })
    ) {
      return false
    }

    dispatch(modalActions.closeModal())
    const ptpCopy = { ...participant }
    const { phone } = ptpCopy
    if (!phone) ptpCopy.phone = ''

    currentSite = sitesArr.shift()
    // When editing profile (useFormSitesForPatch), use site from form (sitesArr). Otherwise use view-context siteID to avoid wrong/cached site (disable/enable etc).
    if (isPatch && !useFormSitesForPatch && siteID != null && !Number.isNaN(Number(siteID))) {
      currentSite = Number(siteID)
      ptpCopy.site_id = currentSite
    } else if (isPatch && !useFormSitesForPatch && participant.site_id != null) {
      const participantSiteId = Number(participant.site_id)
      if (!Number.isNaN(participantSiteId)) {
        currentSite = participantSiteId
        ptpCopy.site_id = participantSiteId
      } else {
        ptpCopy.site_id = currentSite
      }
    } else {
      ptpCopy.site_id = currentSite
    }
    ptpCopy.study_id = studyID
    ptpCopy.study_groups = selectedTracks

    const hasParticipantDataDisabled = pDataFields?.disabled
    if (ptpCopy.visit_start_datetime === '') delete ptpCopy.visit_start_datetime
    if (ptpCopy.start_date === '') delete ptpCopy.start_date
    if (hasParticipantDataDisabled) {
      hasParticipantDataDisabled.forEach(data => {
        delete ptpCopy[data.metadata.internal_key]
      })
    }
    if (isPatch && pDataFields) {
      const { mandatory, optional, disabled } = pDataFields
      const cantUpdateArr = [...mandatory, ...optional, ...disabled].filter(field => {
        return !(field.metadata.can_update ?? true)
      })
      cantUpdateArr.forEach(field => {
        delete ptpCopy[field.metadata.internal_key]
      })
    }

    const successMessage = !hideSuccessMessage ? `Successfully ${isPatch ? 'edited' : 'added'} participant` : undefined

    if (armId) {
      ptpCopy.study_schedule_id = armId
    }

    if (ptpCopy.visit_start_datetime) {
      const formatted = ptpCopy.visit_start_datetime?.replace('T', ' ')
      ptpCopy.visit_start_datetime = formatted
    }

    if (hasStudyPromotion && studyVersionID) {
      ptpCopy.study_version_id = studyVersionID
    }

    if (ptpCopy.unit_height && ptpCopy.unit_weight) {
      ptpCopy.height += ` ${ptpCopy.unit_height}`
      ptpCopy.weight += ` ${ptpCopy.unit_weight}`
      delete ptpCopy.unit_height
      delete ptpCopy.unit_weight
    }

    if (typeof ptpCopy.date_of_birth === 'string' && ptpCopy.date_of_birth.length === 4) {
      ptpCopy.date_of_birth = `${ptpCopy.date_of_birth}-01-01`
    }

    dispatch(sendingActions.startSender())
    return dispatch(
      request({
        method: isPatch ? 'PATCH' : 'POST',
        successMessage,
        url: `/control/${isPatch ? `studies/${studyID}/sites/${currentSite}/participants/${participantID}` : 'participants'
          }`,
        failMessage: 'Error saving participant',
        body: JSON.stringify(ptpCopy),
        success: response => {
          delete participant.password
          while (sitesArr.length > 0) {
            dispatch(
              saveToDatabase({
                studyID,
                siteID,
                participant,
                checkedCohorts: _checkedCohorts,
                participantID,
                sitesArr,
                pDataFields,
                isNotLeaving,
                hideSuccessMessage,
                armId,
              }),
            )
          }
          dispatch(sendingActions.stopSender())
          if (
            participantField &&
            typeof participant[participantField] !== 'undefined' &&
            participant[participantField] !== null
          )
            dispatch(updateField(participantField, participant[participantField]))
          if (drawer.open && participantField === 'opt_out') dispatch(ptpActions.fetchPtps(studyID))
          setTimeout(() => {
            if (redirect) {
              browserHistory.push(`${redirect}?ptpId=${response.id}`)
            } else if (!isNotLeaving) {
              browserHistory.push(`/studies/${studyID}/${studyVersionID}/participants`)
            }
            dispatch(sendingActions.resetSender())
          }, REDIRECT_DELAY)
        },
        fail: (res, content) => {
          dispatch(notyActions.showError({ text: generateNotyMessage(content.message, false) }))
          dispatch(sendingActions.resetSender())
        },
        hasLoader: true,
        loadingKey: 'request',
        hasSender: true,
      }),
    )
  }
}

function openSaveModal({
  studyID,
  siteID,
  participant,
  checkedCohorts: _checkedCohorts,
  participantID,
  consentRequired,
  sites,
  pDataFields,
  isNotLeaving,
  ptpField,
  hasVisitSupport,
  redirect = null,
  hideSuccessMessage,
  armId,
  useFormSitesForPatch = false,
}) {
  const _sitesArr = Object.keys(sites)
  const hasSites = _sitesArr.length > 0 && _sitesArr[0] !== 'null'
  const sitesFromState = hasSites ? _sitesArr.map(idString => parseInt(idString, 10)) : []
  return dispatch => {
    if ('id' in participant) {
      // When editing profile (useFormSitesForPatch), use form's selected site(s). Otherwise use view-context site to avoid cached wrong site (disable/enable etc).
      const viewSiteId = siteID != null && !Number.isNaN(Number(siteID)) ? Number(siteID) : null
      const participantSiteId = participant.site_id != null ? Number(participant.site_id) : null
      const sitesArr =
        useFormSitesForPatch && sitesFromState.length > 0
          ? sitesFromState
          : viewSiteId != null
            ? [viewSiteId]
            : participantSiteId != null && !Number.isNaN(participantSiteId)
              ? [participantSiteId]
              : sitesFromState
      return dispatch(
        saveToDatabase({
          studyID,
          siteID,
          participant,
          checkedCohorts: _checkedCohorts,
          participantID,
          isPatch: true,
          sitesArr,
          pDataFields,
          isNotLeaving,
          participantField: ptpField,
          hasVisitSupport,
          hideSuccessMessage,
          armId,
          useFormSitesForPatch,
        }),
      )
    }
    const onConfirm = () => {
      dispatch(
        saveToDatabase({
          studyID,
          siteID,
          participant,
          checkedCohorts: _checkedCohorts,
          participantID: null,
          isPatch: false,
          sitesArr: sitesFromState,
          pDataFields,
          isNotLeaving,
          hasVisitSupport,
          redirect,
          hideSuccessMessage,
          armId,
        }),
      )
    }
    if (consentRequired) {
      onConfirm()
    } else {
      dispatch(
        modalActions.openModal({
          content: MODAL_CONTENT_MAP.addStudyParticipant,
          confirmButton: MODAL_BUTTONS_MAP.confirm,
          cancelButton: MODAL_BUTTONS_MAP.cancel,
          className: MODAL_CLASSES_MAP.manualConsentConfirmation,
          onConfirm,
        }),
      )
    }
  }
}

export function downloadParticipantData({
  type,
  studyId,
  ptpIdArr,
  siteId,
  scheduleDate,
  formatType = 'long',
  resType = 'blob',
}) {
  let url
  let name
  let from
  let to
  let ptpQueryParams = ''
  if (ptpIdArr) {
    ptpIdArr.forEach(ptpId => {
      ptpQueryParams += `&participant_id=${ptpId}`
    })
  }
  const formatParam = `&format_type=${formatType}`
  if (type === 'diary') {
    if (scheduleDate && !scheduleDate.allDate) {
      if (typeof scheduleDate === 'string') {
        from = scheduleDate
        to = scheduleDate
      } else {
        from = moment(scheduleDate.from).format(DATE_FORMAT_MAP.datePicker)
        to = moment(scheduleDate.to).format(DATE_FORMAT_MAP.datePicker)
      }
      url = `/control/data/ediary?study_id=${studyId}&site_id=${siteId}&from=${from}&to=${to}${formatParam}${ptpQueryParams}`
      name = `_participant_diary_single_response`
    } else {
      // download entire responses from the participant
      url = `/control/data/ediary?study_id=${studyId}&site_id=${siteId}${formatParam}${ptpQueryParams}`
      name = 'participant_diary_all_responses'
    }
  }

  return (dispatch, getState) => {
    const { study, location } = getState()
    const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
    const studyVersionID = getStudyVersionID(location)

    const getQueryParams = () => {
      if (hasStudyPromotion) {
        return `&study_version_id=${studyVersionID}`
      }
      return ''
    }

    url = `${url}${getQueryParams()}`
    const success = (blob, fileName) => {
      if (resType === 'text') {
        dispatch(
          notyActions.showSuccess({
            text: generateNotyMessage(blob, true),
          }),
        )
      } else {
        if (blob.type === BLOB_TYPE_MAP.zip) {
          downloadBlob(blob, `study_${studyId}${name}_${today(true)}.zip`, fileName)
        } else {
          downloadBlob(blob, `study_${studyId}${name}_${today(true)}.csv`, fileName)
        }
      }
    }
    dispatch(loadingActions.startLoader(true, type))
    return dispatch(
      request({
        url,
        resType,
        success,
      }),
    ).then(() => {
      dispatch(loadingActions.stopLoader(true, type))
    })
  }
}

function fetchParticipantVersionHistory(studyID, ptpID) {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: `/control/studies/${studyID}/participants_version_history/participants/${ptpID}`,
        failMessage: 'Error fetching participant version history.',
        success: json => {
          dispatch(setParticipantHistory(json.ptp_version_history))
        },
      }),
    )
  }
}

function fetchParticipant(studyID, siteID, ptpID) {
  return (dispatch, getState) => {
    const { study } = getState()
    const { instrument_architecture_version: instrumentArchitectureVersion } = study?.currentStudy?.config || {}
    if (instrumentArchitectureVersion > DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION) {
      return dispatch(
        request({
          method: 'GET',
          url: `/control/v2/studies/${studyID}/sites/${siteID}/participants/${ptpID}`,
          failMessage: 'Error fetching participant.',
          success: participant => {
            dispatch({
              type: SET_V2_PARTICIPANT,
              payload: participant,
            })
            dispatch(setV2ParticipantInstruments(participant.instruments))
            dispatch(setV2ParticipantAnnouncements(participant.instruments))
            dispatch(fetchParticipantVersionHistory(studyID, ptpID))
            if (participant.study_groups) {
              dispatch(initializeCheckedCohorts(Object.keys(participant.study_groups)))
            }
          },
        }),
      )
    } else {
      return dispatch(
        request({
          method: 'GET',
          url: `/control/studies/${studyID}/sites/${siteID}/participants/${ptpID}`,
          failMessage: 'Error fetching participant.',
          success: participant => {
            dispatch({
              type: SET_PARTICIPANT,
              payload: participant,
            })
            dispatch(setParticipantInstruments(participant.instruments))
            dispatch(setParticipantAnnouncements(participant.announcements))
            dispatch(fetchParticipantVersionHistory(studyID, ptpID))
            if (participant.study_groups) {
              dispatch(initializeCheckedCohorts(Object.keys(participant.study_groups)))
            }
          },
        }),
      )
    }
  }
}

function fetchPtpPayments(studyID, ptpID, isInSuperGemStudy = false) {
  const fetchHistoryFunc = () => {
    if (isInSuperGemStudy) {
      return _fetchTreasureChestPayoutHistory(studyID, false)
    }
    return _fetchPayments(studyID, false)
  }
  const payloadKey = isInSuperGemStudy ? 'completed_super_gem_payouts' : 'payout_info'
  return dispatch => {
    return dispatch(fetchHistoryFunc()).then(json => {
      dispatch({
        type: SET_PTP_PAYMENTS,
        payload: getFormattedPayments(
          json ? json[payloadKey].filter(pmt => ptpID === Number(pmt.participant.id)) : [],
          true, // hideName
          isInSuperGemStudy, // isGiftcards
          isInSuperGemStudy, // isDonations
        ),
      })
      dispatch(loadingActions.stopLoader(true, 'pendingPayments'))
    })
  }
}

const getLocaleOptions = locales => {
  const localesMap = {}
  const list = locales
    ? locales
      .map(option => {
        const { applanga, id, locale, name } = option
        localesMap[id] = { text: name, value: id, key: id }
        return {
          key: id,
          text: name,
          value: id,
          id,
          locale,
          applanga,
        }
      })
      .sort((a, b) => a.text.localeCompare(b.text))
    : []
  return { list, localesMap }
}

export const fetchLocales = () => {
  return dispatch => {
    const success = payload => {
      const { list, localesMap } = getLocaleOptions(payload.languages)
      dispatch({
        type: SET_LOCALES,
        locales: list,
        localesMap,
      })
    }
    return dispatch(
      request({
        method: 'GET',
        url: '/control/locales',
        success,
      }),
    )
  }
}

export const fetchVisitTemplates = studyID => {
  return (dispatch, getState) => {
    const success = payload => {
      dispatch({
        type: SET_VISIT_TEMPLATES,
        visits: payload.study_visit_schedule,
      })
      const { study } = getState() || {}
      const isVisitCycle = study?.currentStudy?.config?.visit_support?.version > 1
      dispatch(setScheduleVisitTemplates(payload, isVisitCycle))
    }
    return dispatch(_fetchVisitSchedules(studyID, success))
  }
}

export const fetchFiles = (studyID, siteID, ptpID) => {
  return (dispatch) => {
    const success = payload => {
      dispatch({
        type: SET_FILES,
        files: payload.participant_files || [],
      })
    }
    return dispatch(
      request({
        method: 'GET',
        url: `/control/studies/${studyID}/sites/${siteID}/participant_files/${ptpID}`,
        success,
      }),
    )
  }
}

export const fetchParticipantVisits = (studyID, ptpID, isAdhocPage = false, isUpdate = false, ssvpID) => {
  return dispatch => {
    const success = payload => {
      dispatch({
        type: SET_PARTICIPANT_VISITS,
        visits: payload,
        isAdhocPage,
      })
      dispatch({
        type: SET_PARTICIPANT_VISIT_CYCLES,
        visitCycles: payload?.ss_ptp_cycles,
      })
      if (isUpdate) {
        dispatch(setAdhocVisit(payload, ssvpID))
      }
    }
    return dispatch(
      request({
        method: 'GET',
        url: `/control/studies/${studyID}/study_schedules_visits/participants/${ptpID}`,
        success,
        hasLoader: isAdhocPage,
        loadingKey: isAdhocPage ? 'participantVisits' : undefined,
      }),
    )
  }
}

//
// Visit Instruments - Actions
//
export const fetchVisitInstruments = (studyID, ptpID, visitIdForUrl, stateKeyVisitId) => {
  return dispatch => {
    const success = payload => {
      // Normalize empty/success-only responses to a consistent shape expected by the UI
      let normalizedPayload = payload
      const hasDirectInstrumentsArray = Array.isArray(payload?.instruments)
      const hasNestedInstrumentsArray = Array.isArray(payload?.[visitIdForUrl]?.instruments)
      const isEmptyObject = payload && typeof payload === 'object' && Object.keys(payload).length === 0
      const isSuccessOnly = payload && typeof payload === 'object' && payload.message === 'SUCCESS'

      if (!hasDirectInstrumentsArray && !hasNestedInstrumentsArray && (isEmptyObject || isSuccessOnly)) {
        normalizedPayload = { instruments: [] }
      } else if (!hasDirectInstrumentsArray && hasNestedInstrumentsArray) {
        // Unwrap payload[visitIdForUrl] so the UI can read instruments at root
        normalizedPayload = payload?.[visitIdForUrl] || { instruments: [] }
      } else if (!hasDirectInstrumentsArray) {
        // Fallback: if payload has a single key with instruments, unwrap it
        const onlyKey =
          payload && typeof payload === 'object' ? Object.keys(payload).find(k => k !== 'message') : undefined
        if (onlyKey && Array.isArray(payload?.[onlyKey]?.instruments)) {
          normalizedPayload = payload[onlyKey]
        }
      }

      const stateKey = stateKeyVisitId || visitIdForUrl
      dispatch({ type: SET_VISIT_INSTRUMENTS, visitId: stateKey, payload: normalizedPayload })
      dispatch({ type: SET_VISIT_INSTRUMENTS_LOADING, visitId: stateKey, isLoading: false })
    }
    const fail = () => {
      const stateKey = stateKeyVisitId || visitIdForUrl
      dispatch({ type: SET_VISIT_INSTRUMENTS_LOADING, visitId: stateKey, isLoading: false })
    }
    const stateKey = stateKeyVisitId || visitIdForUrl
    dispatch({ type: SET_VISIT_INSTRUMENTS_LOADING, visitId: stateKey, isLoading: true })
    return dispatch(
      request({
        method: 'GET',
        // Backend endpoint expected to proxy to new service
        url: `/control/studies/${studyID}/participants/${ptpID}/visit_instruments/${visitIdForUrl}`,
        success,
        fail,
      }),
    )
  }
}

const updatePreviousScheduleVisit = (studyId, ssvId, ptpId, body, onSuccess = () => { }) => dispatch => {
  const success = () => {
    onSuccess()
    dispatch(fetchParticipantVisits(studyId, ptpId))
  }
  return dispatch(patchParticipantScheduleVisit(studyId, ssvId, ptpId, body, success))
}

export const formatSSVisits = obj =>
  objMapToList(obj, value => value).reduce((acc, item) => [...acc, ...objMapToList(item, value => value)], [])

/** Flatten scheduled/adhoc visit payloads; missing or non-list values become []. */
export const normalizeVisitCollection = collection => {
  if (Array.isArray(collection)) return collection.filter(Boolean)
  if (!collection || typeof collection !== 'object') return []

  return Object.values(collection).reduce((result, item) => {
    if (!item) return result
    if (Array.isArray(item)) return [...result, ...item.filter(Boolean)]
    if (typeof item !== 'object') return result
    if (item.ssvp_id) return [...result, item]
    return [...result, ...Object.values(item).filter(value => value && typeof value === 'object')]
  }, [])
}

const formatVisits = (visits, isAdhocPage) => {
  const ptpVisitsArr = normalizeVisitCollection(visits?.ss_visits_participant)
  const adhocVisits = normalizeVisitCollection(visits?.ss_visits_participant_adhoc)

  return [...ptpVisitsArr, ...adhocVisits].map((visit, idx) => {
    const {
      is_adhoc,
      name,
      participant_visit_metadata,
      visit_datetime,
      actual_visit_datetime,
      status_reason,
      ssvp_id,
      study_schedule_visit_id,
      visit_day,
      status = '',
      is_update_allowed = true,
    } = visit || {}
    const visitName = (participant_visit_metadata?.visit_name || name) ?? `Visit ${ssvp_id}`
    const visitTime = moment.utc(visit_datetime)
    const actualVisitTime = moment.utc(actual_visit_datetime)
    const isDayAfter = moment().isAfter(visitTime, 'day')
    const className =
      status !== STATUS_OPTIONS.Missed && status !== STATUS_OPTIONS.Confirmed && !actual_visit_datetime && isDayAfter
        ? 'ptp-visit-cell-highlight'
        : ''
    // GOLD-11452: API sends actual_visit_datetime in UTC; show in viewer's local timezone.
    const actualDateDisplay = actual_visit_datetime
      ? moment(actual_visit_datetime).format(DATE_FORMAT_MAP.main)
      : 'N/A'
    const row = [
      {
        key: 'visitName',
        value: visitName,
        sortValue: visitName,
        visitId: ssvp_id,
        studyScheduleVisitId: study_schedule_visit_id,
        participantVisitMetadata: participant_visit_metadata,
        isAdhoc: is_adhoc,
        className: `ptp-visit-name ${className}`,
      },
      isAdhocPage ? { key: 'visitDay', value: visit_day ?? 'N/A', className: `ptp-visit-day ${className}` } : null,
      {
        key: 'scheduledDate',
        value: `${visitTime.format(DATE_FORMAT_MAP.main)}`,
        sortValue: visitTime.valueOf(),
        className,
      },
      {
        key: 'actualDate',
        value: actualDateDisplay,
        sortValue: actualVisitTime.valueOf(),
        className,
      },
      !isAdhocPage
        ? {
          key: 'status',
          value: status || (isDayAfter ? STATUS_OPTIONS.Passed : STATUS_OPTIONS.Scheduled),
          className: `ptp-visit-status ${className}`,
          statusReasonValue: status_reason,
        }
        : null,
      !isAdhocPage ? { key: 'action', type: '', title: 'Action', className, isUpdateAllowed: is_update_allowed } : null,
      isAdhocPage
        ? {
          key: 'status',
          value: status || (isDayAfter ? STATUS_OPTIONS.Passed : STATUS_OPTIONS.Scheduled),
          className: `ptp-visit-status ${className}`,
          statusReasonValue: status_reason,
        }
        : null,
    ]
    return row.filter(obj => obj)
  })
}

const parseFiles = files => {
  if (!files || !Array.isArray(files) || files.length === 0) {
    return []
  }
  const resultFiles = files
    .filter(file => file && file.file_name) // Filter out invalid files
    .map(file => {
      const { file_name, original_file_name, tag_name, url } = file
      const match = file_name.match(fileTypeRegex)
      const type = match ? match[0] : 'file' // Default to 'file' if no extension found
      const fileRow = [
        { key: 'type', value: type, sortValue: type, className: 'file-type' },
        { key: 'fileName', value: original_file_name || file_name, sortValue: original_file_name || file_name, className: 'ptp-file-name' },
        { key: 'category', value: tag_name || '', sortValue: tag_name || '' },
        { key: 'action', value: url || '', sortValue: null, className: 'file-download' },
      ]
      return fileRow
    })
  return resultFiles
}

function initializeEditPage(studyID, siteID, ptpID, isInSuperGemStudy) {
  return dispatch => {
    dispatch(loadingActions.startLoader(true))
    return dispatch(fetchParticipant(studyID, siteID, ptpID)).then(() => {
      return dispatch(fetchPtpPayments(studyID, ptpID, isInSuperGemStudy)).then(() => {
        return dispatch(fetchLocales()).then(() => {
          return dispatch(loadingActions.stopLoader(true))
        })
      })
    })
  }
}

const markInstrumentComplete = ({ ptpID, studyID, siteID, instrumentsToComplete }, successCallback) => dispatch => {
  const instrumentsIdqArr = Object.keys(instrumentsToComplete)
  const _body = { idq_ids: instrumentsIdqArr }
  const success = () => {
    return dispatch(fetchParticipant(studyID, siteID, ptpID)).then(() => {
      successCallback()
      dispatch(loadingActions.stopLoader(true, 'participantInstruments'))
    })
  }

  const fail = (res, content) => {
    dispatch(loadingActions.stopLoader(true, 'participantInstruments'))
    dispatch(notyActions.showError({ text: generateNotyMessage(content.message, false) }))
  }
  dispatch(loadingActions.startLoader(true, 'participantInstruments'))
  dispatch(
    request({
      method: 'PATCH',
      url: `/control/studies/${studyID}/sites/${siteID}/participants/${ptpID}/instruments`,
      body: JSON.stringify(_body),
      success,
      fail,
    }),
  )
}

const regularDataValidation = {
  fname: 'fname',
  lname: 'lname',
  email: 'email',
  phone: 'phone',
  date_of_birth: 'date_of_birth',
  start_date: 'start_date',
  site_id: 'site_id',
  default_locale: 'default_locale',
  pre_screen_id: 'pre_screen_id',
  visit_start_datetime: 'visit_start_datetime',
  start_visit_id: 'start_visit_id',
  weight: 'weight',
  height: 'height',
}

const newDataValidation = {
  subject_id: 'subject_id',
  study_groups: 'study_groups',
}

const keysToSkipValidationsOnUpdate = [regularDataValidation.visit_start_datetime, regularDataValidation.start_visit_id]

const newMandatoryPtpFields = [
  {
    display_label: 'Locale',
    type: 'text',
    metadata: {
      internal_key: 'default_locale',
      custom_validations: '',
      is_custom: false,
      default_value: '',
      can_update: true,
    },
  },
]

export function validateParticipant({
  participant,
  dispatch,
  sitesArr,
  pDataFields = DEFAULT_PTP_DATA_FIELDS_CONFIG,
  selectedTracks,
  isPatch,
  isVisitCycleSupported,
}) {
  const {
    fname,
    lname,
    email,
    phone,
    weight,
    height,
    unit_height,
    unit_weight,
    date_of_birth,
    visit_start_datetime,
    start_visit_id,
    subject_id,
    pre_screen_id,
    default_locale,
    start_date,
  } = participant
  let errorMessage = ''
  const mandatoryPtpFields = [...pDataFields?.mandatory, ...newMandatoryPtpFields]

  const defaultValidationKeys = Object.keys(regularDataValidation)

  let mandatoryValidations =
    mandatoryPtpFields?.length > 0
      ? defaultValidationKeys.filter(key => mandatoryPtpFields.map(data => data.metadata.internal_key).includes(key))
      : defaultValidationKeys

  if (mandatoryPtpFields?.length > 0) {
    const additionalValidationKeys = Object.keys(newDataValidation).filter(key =>
      mandatoryPtpFields.map(data => data.metadata.internal_key).includes(key),
    )
    if (additionalValidationKeys.length) mandatoryValidations.push(...additionalValidationKeys)
  }

  if (isPatch) {
    mandatoryValidations = mandatoryValidations.filter(key => {
      return !keysToSkipValidationsOnUpdate.includes(key)
    })
  }

  /**
   * We will create a map of custom validations if any field has any
   * custom validation (e.g. regex pattern the value must meet)
   */
  const customValidationsMap = mandatoryPtpFields.reduce((acc, currentField) => {
    const { metadata } = currentField
    const { custom_validations, internal_key } = metadata
    const obj = { ...acc }
    if (custom_validations) {
      obj[internal_key] = custom_validations
    }
    return obj
  }, {})

  const buildRegexFromFormat = (formatString) => {
    if (!formatString || typeof formatString !== 'string') return null
    const tokenRegex = /\{([^}]+)\}/g
    let cursor = 0
    let regexStr = ''
    const escapeConst = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const pushConst = (end) => {
      if (end > cursor) {
        const piece = formatString.substring(cursor, end)
        regexStr += escapeConst(piece)
      }
    }

    // Define handlers for different regex checks
    const handlers = [
      // STUDY_ID / SITE_ID, optionally with a fixed length: {STUDY_ID} or {STUDY_ID:4}
      {
        test: t => /^STUDY_ID(?::\d+)?$/.test(t) || /^SITE_ID(?::\d+)?$/.test(t),
        build: t => {
          const parts = t.split(':')
          if (parts.length === 2) {
            const len = parseInt(parts[1], 10) || 1
            return REGEX_PARTS.alnumUpper(len)
          }
          // default: any non-empty alpha-num / dash sequence
          return REGEX_PARTS.alnumUpper()
        },
      },
      // Editable tokens used by PatternInput: LETTER and DIGITS
      {
        test: t => /^LETTER(?::\d+)?$/.test(t),
        build: t => {
          const parts = t.split(':')
          const len = parts.length === 2 ? parseInt(parts[1], 10) || 1 : 1
          // PatternInput forces LETTER segments to uppercase A–Z
          return REGEX_PARTS.upper(len)
        },
      },
      {
        test: t => /^DIGITS(?::\d+)?$/.test(t),
        build: t => {
          const parts = t.split(':')
          const len = parts.length === 2 ? parseInt(parts[1], 10) || 1 : 1
          return REGEX_PARTS.digits(len)
        },
      },
      {
        test: t => /^INCR:(.+)$/.test(t),
        build: t => {
          // INCR:startValue - the value after colon is the starting value (e.g., 01, 001)
          // The length is determined by the format of the starting value
          const m = t.match(/^INCR:(.+)$/)
          const startValue = m && m[1] ? m[1] : '01'
          const len = startValue.length
          return REGEX_PARTS.digits(len)
        },
      },
      { 
        test: t => /^LETTERS:(\d+)$/.test(t), 
        build: t => REGEX_PARTS.upper(parseInt(t.replace(/^LETTERS:/, ''), 10) || 1)
      },
      {
        test: t => /^LETTERS_LOWER:(\d+)$/.test(t),
        build: t => REGEX_PARTS.lower(parseInt(t.replace(/^LETTERS_LOWER:/, ''), 10) || 1),
      },
      {
        test: t => /^LETTERS_MIXED:(\d+)$/.test(t),
        build: t => REGEX_PARTS.mixed(parseInt(t.replace(/^LETTERS_MIXED:/, ''), 10) || 1),
      },
      { 
        test: t => /^DIGITS:(\d+)$/.test(t), 
        build: t => REGEX_PARTS.digits(parseInt(t.replace(/^DIGITS:/, ''), 10) || 1)
      },
      // ALNUM tokens must contain at least one digit (A12-style, not AAA).
      // Use lookahead that only scans within the segment (`[A-Z0-9]*\\d`), so
      // the required digit cannot \"leak\" into later parts of the ID.
      {
        test: t => /^ALNUM:(\d+)$/.test(t),
        build: t => {
          const len = parseInt(t.replace(/^ALNUM:/, ''), 10) || 1
          return REGEX_PARTS.alnumUpperWithDigit(len)
        },
      },
      {
        test: t => /^ALNUM_LOWER:(\d+)$/.test(t),
        build: t => {
          const len = parseInt(t.replace(/^ALNUM_LOWER:/, ''), 10) || 1
          return REGEX_PARTS.alnumLowerWithDigit(len)
        },
      },
      {
        test: t => /^ALNUM_MIXED:(\d+)$/.test(t),
        build: t => {
          const len = parseInt(t.replace(/^ALNUM_MIXED:/, ''), 10) || 1
          return REGEX_PARTS.alnumMixedWithDigit(len)
        },
      },
    ]

    let match
    while ((match = tokenRegex.exec(formatString)) !== null) {
      pushConst(match.index)
      const token = match[1]
      const handler = handlers.find(h => h.test(token))
      regexStr += handler ? handler.build(token) : escapeConst(match[0])
      cursor = match.index + match[0].length
    }
    pushConst(formatString.length)
    return regexStr ? new RegExp(`^${regexStr}$`) : null
  }



  const getAutoGenFormatFor = (key) => {
    const all = [...(pDataFields?.mandatory || []), ...(pDataFields?.optional || [])]
    const field = all.find(f => f?.metadata?.internal_key === key)
    return field?.metadata?.auto_gen_format || ''
  }

  const preScreenIdFmt = getAutoGenFormatFor(PARTICIPANT_KEYS.PRE_SCREEN_ID)
  if (preScreenIdFmt) {
    const rx = buildRegexFromFormat(preScreenIdFmt)
    if (rx) customValidationsMap.pre_screen_id = rx
  }

  const subjectIdFmt = getAutoGenFormatFor(PARTICIPANT_KEYS.SUBJECT_ID)
  if (subjectIdFmt) {
    const rx = buildRegexFromFormat(subjectIdFmt)
    if (rx) customValidationsMap.subject_id = rx
  }
  const getVisitStartDatetime = () => {
    // if (isPatch && isVisitCycleSupported) {
    //   return
    // }
    if (isVisitCycleSupported) {
      return visit_start_datetime
    }
    return visit_start_datetime
    // TODO: potentially needs to be updated to have support to old regular visits ( if needed )
    // return isPatch ? undefined : visit_start_datetime
  }

  const errors = getParticipantErrors({
    participantData: {
      fname,
      lname,
      email,
      phone,
      date_of_birth,
      site_id: sitesArr,
      // ...(!isPatch && { visit_start_datetime: getVisitStartDatetime() }),
      visit_start_datetime: getVisitStartDatetime(),
      start_visit_id,
      subject_id,
      pre_screen_id,
      default_locale,
      start_date,
      study_groups: selectedTracks,
      weight,
      height,
      unit_height,
      unit_weight,
    },
    mandatoryValidations,
    customValidationsMap,
    isVisitCycleSupported,
  })

  if (errors && errors.length > 0) {
    errorMessage += `${errors.join('<br>')}<br>`
  }
  if (errorMessage !== '' && dispatch) {
    dispatch(notyActions.showError({ text: generateNotyMessage(errorMessage, false) }))
  }
  return errorMessage === ''
}

function updateField(field, value) {
  return dispatch => {
    dispatch({
      type: UPDATE_FIELD,
      field,
      value,
    })
  }
}

function triggerPasswordReset({ id, username }) {
  return dispatch => {
    dispatch(
      request({
        method: 'POST',
        url: `/control/participants/${id}/forgot_password`,
        body: JSON.stringify({ username, participant_id: id }),
        successMessage: 'Reset password email sent to participant',
      }),
    )
  }
}

function validateSubjectId({ studyId, subjectId, participantId, selectedSites = [] }) {
  const selectedSite = selectedSites?.[0] || null

  return dispatch => {
    const parameterObj = {
      study_id: studyId,
      subject_id: subjectId,
      participant_id: participantId,
      site_id: selectedSite
    }

    Object.keys(parameterObj).forEach(paramKey => {
      if (!parameterObj[paramKey]) delete parameterObj[paramKey]
    })
    const searchParams = new URLSearchParams(parameterObj)
    const queryParamStr = searchParams.toString()

    dispatch(
      request({
        method: 'GET',
        url: `/control/check-subject-id?${queryParamStr}`,
        success: json => {
          dispatch(setSubjectIdStatus(!json?.exists))
        },
      }),
    )
  }
}

function triggerResendInvite({ username, ptpId }) {
  return dispatch => {
    dispatch(
      request({
        method: 'POST',
        url: `/control/resend_invite`,
        body: JSON.stringify({ username, participant_id: ptpId }),
        successMessage: 'Resend invite email sent to participant',
      }),
    )
  }
}

function generateTempPassword({ studyID, ptpID, successCallback }) {
  return dispatch => {
    dispatch(
      request({
        method: 'POST',
        url: `/control/studies/${studyID}/participants/${ptpID}/temporary_password`,
        success: response => successCallback(response),
        hasLoader: true,
        loadingKey: 'tempPassword',
      }),
    )
  }
}

function initializePage({ fname = '', lname = '', defaultPtpValMap = {} }) {
  return dispatch => {
    return dispatch({
      type: INITIALIZE_CREATE_PTP,
      fname,
      lname,
      defaultPtpValMap,
    })
  }
}

export function initializeCheckedCohorts(cohorts) {
  return {
    type: INITIALIZE_CHECKED_COHORTS,
    cohorts,
  }
}

function resetInstrumentTimeline() {
  return {
    type: RESET_INSTRUMENT_TIMELINE,
  }
}

function setInstrumentTimeline(payload) {
  return {
    type: SET_INSTRUMENT_TIMELINE,
    payload,
  }
}

function setV2InstrumentTimeline(payload) {
  return {
    type: SET_V2_INSTRUMENT_TIMELINE,
    payload,
  }
}

function resetDiaryTimeline() {
  return {
    type: RESET_DIARY_TIMELINE,
  }
}

function setDiaryTimeline(payload) {
  return {
    type: SET_DIARY_TIMELINE,
    payload,
  }
}

export function _fetchInstrumentTimeline(instrumentId) {
  return dispatch => {
    const success = jsonBody => {
      return Promise.resolve(jsonBody.instruments_timeline)
    }
    return dispatch(
      request({
        url: `/control/instruments_queue/${instrumentId}/timeline`,
        success,
      }),
    ).then(payload => {
      return Promise.resolve(payload)
    })
  }
}

export function _fetchInstanceTimeline(instanceId) {
  return dispatch => {
    const success = jsonBody => {
      return Promise.resolve(jsonBody.instruments_timeline)
    }
    return dispatch(
      request({
        url: `/control/instances/${instanceId}/timeline`,
        success,
      }),
    ).then(payload => {
      return Promise.resolve(payload)
    })
  }
}

export const fetchInstrumentTimeline = instrumentId => (dispatch, getState) => {
  const { study } = getState()
  const { instrument_architecture_version: instrumentArchitectureVersion } = study?.currentStudy?.config || {}

  dispatch(loadingActions.startLoader(true, 'instrumentTimeline'))
  dispatch(resetInstrumentTimeline())
  dispatch(
    instrumentArchitectureVersion > DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
      ? _fetchInstanceTimeline(instrumentId)
      : _fetchInstrumentTimeline(instrumentId),
  ).then(payload => {
    dispatch(
      instrumentArchitectureVersion > DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
        ? setV2InstrumentTimeline(payload)
        : setInstrumentTimeline(payload),
    )
    dispatch(loadingActions.stopLoader(true, 'instrumentTimeline'))
  })
}

export function resetInstrumentSubmission() {
  return {
    type: RESET_INSTRUMENT_SUBMISSION,
  }
}

function setInstrumentSubmission(payload) {
  return {
    type: SET_INSTRUMENT_SUBMISSION,
    payload,
  }
}

function setInstrumentSubmissionError({ message, traceId }) {
  return {
    type: SET_INSTRUMENT_SUBMISSION_ERROR,
    message,
    traceId,
  }
}

let latestInstrumentSubmissionRequestId = 0

export const fetchInstrumentSubmission = ({
  studyID,
  ptpID,
  instanceId,
  requestId,
  onStale,
}) => dispatch => {
  const activeRequestId = requestId || ++latestInstrumentSubmissionRequestId
  latestInstrumentSubmissionRequestId = activeRequestId

  dispatch(loadingActions.startLoader(true, 'instrumentSubmission'))
  dispatch(resetInstrumentSubmission())

  const success = jsonBody => {
    if (onStale?.() || activeRequestId !== latestInstrumentSubmissionRequestId) {
      return
    }
    dispatch(setInstrumentSubmission(jsonBody))
    dispatch(loadingActions.stopLoader(true, 'instrumentSubmission'))
  }

  const fail = (response, content = {}) => {
    if (onStale?.() || activeRequestId !== latestInstrumentSubmissionRequestId) {
      return
    }
    dispatch(
      setInstrumentSubmissionError({
        message: content.message || 'Unable to load instrument responses. Please try again or contact support.',
        traceId: content.trace_id,
      }),
    )
    dispatch(loadingActions.stopLoader(true, 'instrumentSubmission'))
  }

  return dispatch(
    request({
      method: 'GET',
      url: `/control/v2/studies/${studyID}/participants/${ptpID}/instances/${instanceId}/submission`,
      success,
      fail,
      skipNotySuccess: true,
    }),
  )
}

export const fetchDiaryTimeline = instrumentId => dispatch => {
  dispatch(loadingActions.startLoader(true, 'diaryTimeline'))
  dispatch(resetDiaryTimeline())
  dispatch(_fetchInstrumentTimeline(instrumentId)).then(payload => {
    dispatch(setDiaryTimeline(payload))
    dispatch(loadingActions.stopLoader(true, 'diaryTimeline'))
  })
}

function resetAnnouncementTimeline() {
  return {
    type: RESET_ANNOUNCEMENT_TIMELINE,
  }
}

function setAnnouncementTimeline(payload) {
  return {
    type: SET_ANNOUNCEMENT_TIMELINE,
    payload,
  }
}

function setV2AnnouncementTimeline(payload) {
  return {
    type: SET_V2_ANNOUNCEMENT_TIMELINE,
    payload,
  }
}

export function _fetchAnnouncementTimeline(announcementId) {
  return dispatch => {
    const success = jsonBody => {
      return Promise.resolve(jsonBody.announcements_timeline)
    }
    return dispatch(
      request({
        url: `/control/announcements_queue/${announcementId}/timeline`,
        success,
      }),
    ).then(payload => {
      return Promise.resolve(payload)
    })
  }
}

export const fetchAnnouncementTimeline = instrumentId => (dispatch, getState) => {
  const { study } = getState()
  const { instrument_architecture_version: instrumentArchitectureVersion } = study?.currentStudy?.config || {}

  dispatch(loadingActions.startLoader(true, 'announcementTimeline'))
  dispatch(resetAnnouncementTimeline())
  dispatch(
    instrumentArchitectureVersion > DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
      ? _fetchInstanceTimeline(instrumentId)
      : _fetchAnnouncementTimeline(instrumentId),
  ).then(payload => {
    dispatch(
      instrumentArchitectureVersion > DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
        ? setV2AnnouncementTimeline(payload)
        : setAnnouncementTimeline(payload),
    )
    dispatch(loadingActions.stopLoader(true, 'announcementTimeline'))
  })
}

const parsePtp = participant => {
  const resultPtp = {
    ...participant,
    subject_id: participant.subject_id ?? '',
    pre_screen_id: participant.pre_screen_id ?? ''
  };
  // Parse weight if it exists
  if (participant.weight) {
    const [weightValue, weightUnit] = participant.weight.split(/\s+/)
    resultPtp.weight = parseFloat(weightValue) // Convert to number
    resultPtp.unit_weight = weightUnit || '' // Fallback to empty string if no unit
  }

  // Parse height if it exists
  if (participant.height) {
    const ptpHeightArr = participant.height.split(/\s+/)
    const heightArrLength = ptpHeightArr.length
    resultPtp.height = heightArrLength > 2 ? ptpHeightArr.slice(0, 2).join(' ') : ptpHeightArr[0]
    resultPtp.unit_height = ptpHeightArr[heightArrLength - 1] || ''
  }

  if (participant.date_of_birth && (participant.height || participant.weight)) {
    resultPtp.date_of_birth = participant.date_of_birth.slice(0, 4)
  }

  return resultPtp
}

const parsePtpConsents = ({ consents }) => {
  if (!consents) return []
  const result = consents.map(consent => {
    const {
      completed_date,
      consent_id,
      consent_participant_status,
      consent_title,
      deployed_date,
      consent_version,
    } = consent
    const scheduledDate = deployed_date ? moment(deployed_date).format(DATE_FORMAT_MAP.mainWithDateTime) : ''
    const completedDate = completed_date ? moment(completed_date).format(DATE_FORMAT_MAP.mainWithDateTime) : ''

    const checkboxEnabled = () => {
      return !['awaiting_clinro', 'complete'].includes(consent_participant_status)
    }

    const { text, sortValue } = PARTICIPANT_CONSENT_STATUS_MAP[consent_participant_status]

    const row = [
      { key: 'status', value: text, sortValue },
      {
        key: 'name',
        value: consent_title,
        completed: consent_participant_status === 'complete',
        consentId: consent_id,
        canUpload: checkboxEnabled(),
        consentVersion: consent_version,
      },
      {
        key: 'version',
        value: consent_version,
      },
      { key: 'scheduled', value: scheduledDate },
      { key: 'completion', value: completedDate },
    ]
    return row
  })
  return result
}

function getInitialState(fname = '', lname = '', defaultPtpValMap) {
  return {
    fname,
    lname,
    email: defaultPtpValMap?.email !== undefined ? defaultPtpValMap.email : '',
    phone: defaultPtpValMap?.phone !== undefined ? defaultPtpValMap.phone : '',
    gender: defaultPtpValMap?.gender !== undefined ? defaultPtpValMap.gender : 'n',
    subject_id: defaultPtpValMap?.subject_id !== undefined ? defaultPtpValMap.subject_id : '',
    default_locale: defaultPtpValMap?.default_locale !== undefined ? defaultPtpValMap.default_locale : 'en_US',
    date_of_birth: defaultPtpValMap?.date_of_birth !== undefined ? defaultPtpValMap.date_of_birth : '1980-01-01',
    is_caregiver: defaultPtpValMap?.is_caregiver !== undefined ? defaultPtpValMap.is_caregiver : false,
    is_self_sign_on: false,
    visit_start_datetime: '',
    start_visit_id: '',
    start_date: '',
  }
}

const setParticipantAnnouncements = (ptpAnnouncements = []) => dispatch => {
  dispatch(loadingActions.startLoader(true, 'participantsAnnouncements'))
  const keys = ptpAnnouncements.map(ptpAnnouncement => Object.keys(ptpAnnouncement)).flat()
  const values = ptpAnnouncements.map((ptpAnnouncement, idx) => ptpAnnouncement[keys[idx]]).flat()
  const formattedValues = values.map((item, idx) => ({
    ...item,
    idqId: Number(keys[idx]),
  }))

  const announcementList = formattedValues.map(item => {
    const { title, status, status_timestamp, idqId, info } = item
    const { utc_offset } = info || {}

    const statusDate = moment(status_timestamp).utc()
    const utcStatusDate = getFormattedUTCTimestamp(status_timestamp, utc_offset)

    return [
      { key: 'name', value: title, sortValue: title },
      {
        key: 'status',
        value: COMBINED_INSTRUMENT_TIMELINE_MAP[status] || NOT_APPLICABLE_PLACEHOLDER,
        sortValue: COMBINED_INSTRUMENT_TIMELINE_MAP[status] || 'zz',
      },
      {
        key: 'statusTimestamp',
        value: status_timestamp ? utcStatusDate : undefined,
        sortValue: status_timestamp ? statusDate.valueOf() : 'zz',
        idqId,
      },
    ]
  })
  dispatch({
    type: SET_PARTICIPANT_ANNOUNCEMENTS,
    announcements: announcementList,
  })
  dispatch(loadingActions.stopLoader(true, 'participantsAnnouncements'))
}

const setParticipantHistory = (ptpHistory = []) => dispatch => {
  dispatch(loadingActions.startLoader(true, 'participantsHistory'))

  const historyList = ptpHistory.map(item => {
    const { from_version, to_version, created_timestamp } = item

    const timestampDate = moment(created_timestamp).utc()

    const name = `Moved from ${from_version} to ${to_version}`

    return [
      { key: 'name', value: name, sortValue: name },
      {
        key: 'timestamp',
        value: created_timestamp ? timestampDate.format(DATE_FORMAT_MAP.mainWithDateTime) : undefined,
        sortValue: created_timestamp ? timestampDate.valueOf() : 'zz',
      },
    ]
  })
  dispatch({
    type: SET_PARTICIPANT_HISTORY,
    history: historyList,
  })
  dispatch(loadingActions.stopLoader(true, 'participantsHistory'))
}

const setParticipantInstruments = (ptpInstruments = {}) => dispatch => {
  const { completed, pending } = ptpInstruments
  dispatch(loadingActions.startLoader(true, 'participantInstruments'))
  const instruments = { ...completed, ...pending }
  const hasInstruments = Object.keys(instruments).length > 0

  const list = hasInstruments
    ? Object.keys(instruments).map(key => {
      const instrumentObj = instruments[key]
      instrumentObj.idqId = key
      return instrumentObj
    })
    : []

  const getCycleNumValAndSortVal = (cycleNum, completionDate) => {
    const largeNumber = Number.MAX_SAFE_INTEGER
    if (completionDate) {
      if (cycleNum) {
        return { cycleNum, sortVal: cycleNum }
      }
      return { cycleNum: 'Post-study', sortVal: largeNumber }
    }
    return { cycleNum: '', sortVal: largeNumber + 1 }
  }

  const instrumentList = list.map(item => {
    const {
      completion_timestamp,
      cycle_no,
      idqId,
      status,
      mark_instrument_complete: instrumentMarkedComplete,
      completed_utc_offset,
    } = item // idqId is the instrument deployment queue ID
    const { cycleNum, sortVal: cycleNumSortVal } = getCycleNumValAndSortVal(cycle_no, completion_timestamp)

    const { event_timestamp, event_type, event_utc_offset } = status || {}
    /**
     * As completion_timestamp is UTC,
     * we need to invoke the utc method on the moment object to set it to UTC
     */
    const completionDate = moment(completion_timestamp).utc()
    const eventDate = moment(event_timestamp).utc()
    const utcEventDate = getFormattedUTCTimestamp(event_timestamp, event_utc_offset)

    const getActionValue = () => {
      if (completion_timestamp) {
        if (instrumentMarkedComplete) {
          return 'Marked complete'
        }
        return 'completed'
      }
      return 'canComplete'
    }
    return [
      { key: 'name', value: item.title, sortValue: item.title },
      { key: 'cycleNo', value: cycleNum, sortValue: cycleNumSortVal },
      {
        key: 'completionDate',
        value: completion_timestamp
          ? completionDate.add(completed_utc_offset || 0, 'minutes').format(DATE_FORMAT_MAP.mainWithDateTime)
          : NOT_APPLICABLE_PLACEHOLDER,
        sortValue: completion_timestamp ? completionDate.valueOf() : cycleNumSortVal,
        completed: !!completion_timestamp,
      },
      {
        key: 'status',
        value: COMBINED_INSTRUMENT_TIMELINE_MAP[event_type] || NOT_APPLICABLE_PLACEHOLDER,
        sortValue: COMBINED_INSTRUMENT_TIMELINE_MAP[event_type] || 'zz',
      },
      {
        key: 'statusTimestamp',
        value: event_timestamp ? utcEventDate : null,
        sortValue: event_timestamp ? eventDate.valueOf() : 0,
      },
      {
        key: 'action',
        value: getActionValue(),
        idqId,
        completed: !!completion_timestamp,
      },
    ]
  })
  dispatch({
    type: SET_PARTICIPANT_INSTRUMENTS,
    instruments: instrumentList,
  })
  dispatch(loadingActions.stopLoader(true, 'participantInstruments'))
}

const setV2ParticipantAnnouncements = (ptpInstruments = {}) => dispatch => {
  dispatch(loadingActions.startLoader(true, 'participantsAnnouncements'))
  const hasInstruments = Object.keys(ptpInstruments).length > 0

  const list = hasInstruments
    ? Object.keys(ptpInstruments).map(key => {
      const instrumentObj = ptpInstruments[key]
      instrumentObj.idqId = key
      return instrumentObj
    })
    : []

  const filteredList = list.filter(item => item.instrument_type === INSTRUMENT_TYPE_MAP.announcement)

  const announcementList = filteredList.map(item => {
    const { title, status, idqId } = item
    const { event_timestamp, event_type, event_utc_offset } = status || {}
    /**
     * As completion_timestamp is UTC,
     * we need to invoke the utc method on the moment object to set it to UTC
     */
    const eventDate = moment(event_timestamp).utc()
    const utcEventDate = getFormattedUTCTimestamp(event_timestamp, event_utc_offset)

    return [
      { key: 'name', value: title, sortValue: title },
      {
        key: 'status',
        value: COMBINED_INSTRUMENT_TIMELINE_MAP[event_type] || NOT_APPLICABLE_PLACEHOLDER,
        sortValue: COMBINED_INSTRUMENT_TIMELINE_MAP[event_type] || 'zz',
      },
      {
        key: 'statusTimestamp',
        value: event_timestamp ? utcEventDate : null,
        sortValue: event_timestamp ? eventDate.valueOf() : 0,
        idqId,
      },
    ]
  })

  dispatch({
    type: SET_V2_PARTICIPANT_ANNOUNCEMENTS,
    announcements: announcementList,
  })
  dispatch(loadingActions.stopLoader(true, 'participantsAnnouncements'))
}

const setV2ParticipantInstruments = (ptpInstruments = {}) => dispatch => {
  dispatch(loadingActions.startLoader(true, 'participantInstruments'))
  const hasInstruments = Object.keys(ptpInstruments).length > 0

  const list = hasInstruments
    ? Object.keys(ptpInstruments).map(key => {
      const instrumentObj = ptpInstruments[key]
      instrumentObj.idqId = key
      return instrumentObj
    })
    : []

  const filteredList = list.filter(item => item.instrument_type !== INSTRUMENT_TYPE_MAP.announcement)

  const getCycleNumValAndSortVal = (cycleNum, completionDate) => {
    const largeNumber = Number.MAX_SAFE_INTEGER
    if (completionDate) {
      if (cycleNum) {
        return { cycleNum, sortVal: cycleNum }
      }
      return { cycleNum: 'Post-study', sortVal: largeNumber }
    }
    return { cycleNum: '', sortVal: largeNumber + 1 }
  }

  const instrumentList = filteredList.map(item => {
    const {
      completion_timestamp,
      cycle_no,
      idqId,
      status,
      mark_instrument_complete: instrumentMarkedComplete,
      completed_utc_offset,
    } = item // idqId is the instrument deployment queue ID
    const { cycleNum, sortVal: cycleNumSortVal } = getCycleNumValAndSortVal(cycle_no, completion_timestamp)

    const { event_timestamp, event_type, event_utc_offset } = status || {}
    /**
     * As completion_timestamp is UTC,
     * we need to invoke the utc method on the moment object to set it to UTC
     */
    const completionDate = moment(completion_timestamp).utc()
    const eventDate = moment(event_timestamp).utc()
    const utcEventDate = getFormattedUTCTimestamp(event_timestamp, event_utc_offset)

    const getActionValue = () => {
      if (completion_timestamp) {
        if (instrumentMarkedComplete) {
          return 'Marked complete'
        }
        return 'completed'
      }
      return 'canComplete'
    }
    return [
      { key: 'name', value: item.title, sortValue: item.title },
      { key: 'cycleNo', value: cycleNum, sortValue: cycleNumSortVal },
      {
        key: 'completionDate',
        value: completion_timestamp
          ? completionDate.add(completed_utc_offset || 0, 'minutes').format(DATE_FORMAT_MAP.mainWithDateTime)
          : NOT_APPLICABLE_PLACEHOLDER,
        sortValue: completion_timestamp ? completionDate.valueOf() : cycleNumSortVal,
        completed: !!completion_timestamp,
      },
      {
        key: 'status',
        value: COMBINED_INSTRUMENT_TIMELINE_MAP[event_type] || NOT_APPLICABLE_PLACEHOLDER,
        sortValue: COMBINED_INSTRUMENT_TIMELINE_MAP[event_type] || 'zz',
      },
      {
        key: 'statusTimestamp',
        value: event_timestamp ? utcEventDate : null,
        sortValue: event_timestamp ? eventDate.valueOf() : 0,
      },
      {
        key: 'action',
        value: getActionValue(),
        idqId,
        instanceId: idqId,
        completed: !!completion_timestamp,
        instrumentType: item.instrument_type,
        title: item.title,
        statusTimestamp: event_timestamp
          ? utcEventDate
          : completion_timestamp
            ? completionDate.add(completed_utc_offset || 0, 'minutes').format(DATE_FORMAT_MAP.mainWithDateTime)
            : null,
        statusEventType: event_type,
      },
    ]
  })
  dispatch({
    type: SET_V2_PARTICIPANT_INSTRUMENTS,
    instruments: instrumentList,
  })
  dispatch(loadingActions.stopLoader(true, 'participantInstruments'))
}

function setParticipantDiaries(diaries = {}) {
  const diaryKeys = Object.keys(diaries)
  return diaryKeys
    .map(key => {
      const diary = diaries[key]
      const ptpDiaryKeys = Object.keys(diary)
      return ptpDiaryKeys.map(ptpDiaryKey => {
        const ptpDiary = diary[ptpDiaryKey]
        const { completion_timestamp, completed_utc_offset, schedule_timestamp, title, completed_dep_queue_id } =
          ptpDiary || {}

        /**
         * As completion_timestamp is UTC,
         * we need to invoke the utc method on the moment object to set it to UTC
         */
        let completionDate = completion_timestamp ? moment(completion_timestamp).utc() : ''
        completionDate = completionDate ? completionDate.add(completed_utc_offset || 0, 'minutes') : ''

        const scheduleDate = schedule_timestamp ? moment(schedule_timestamp, DATE_FORMAT_MAP.diaryScheduledDate) : ''

        return [
          { key: 'name', value: title || NOT_APPLICABLE_PLACEHOLDER, sortValue: title },
          {
            key: 'scheduleDate',
            value: scheduleDate ? scheduleDate.format(DATE_FORMAT_MAP.main) : NOT_APPLICABLE_PLACEHOLDER,
            sortValue: scheduleDate ? scheduleDate.valueOf() : Number.MAX_SAFE_INTEGER,
          },
          {
            key: 'completionDate',
            value: completionDate
              ? completionDate.format(DATE_FORMAT_MAP.mainWithDateTime)
              : NOT_APPLICABLE_PLACEHOLDER,
            sortValue: completionDate ? completionDate.valueOf() : Number.MAX_SAFE_INTEGER,
            completed: !!completion_timestamp,
          },
          {
            key: 'action',
            value: 'download',
            id: `${key}-${scheduleDate}`,
            idqId: completed_dep_queue_id,
          },
        ]
      })
    })
    .flat()
}

/**
 * Decline the account deletion request of the participant.
 * @param {Number} ptpId participant id
 * @param {Number} studyID study id
 * @param {Number} siteID site id
 * @returns
 */
const submitCancelDeactivation = ({ ptpId, studyID, siteID }) => dispatch => {
  const success = () => {
    dispatch(fetchParticipant(studyID, siteID, ptpId))
    dispatch(ptpActions.fetchPtps(studyID))
  }
  return dispatch(
    request({
      method: 'POST',
      url: `/control/studies/${studyID}/participants/${ptpId}/decline`,
      successMessage: 'Account Deletion Request Deactivated',
      success,
      hasLoader: true,
    }),
  )
}

//
// Reducers
//

export const participant = (state = {}, action) => {
  let newState
  switch (action.type) {
    case INITIALIZE_CREATE_PTP: {
      return getInitialState(action.fname, action.lname, action.defaultPtpValMap)
    }
    case UPDATE_FIELD:
      newState = { ...state }
      newState[action.field] = action.value
      return newState
    case SET_V2_PARTICIPANT:
    case SET_PARTICIPANT:
      return parsePtp(action.payload)
    case RESET_PARTICIPANT:
      return {}
    default:
      return state
  }
}

export const participantConsents = (state = [], action) => {
  switch (action.type) {
    case SET_V2_PARTICIPANT:
    case SET_PARTICIPANT:
      return parsePtpConsents(action.payload)
    default:
      return state
  }
}

const payments = (state = { totals: {}, pendingTotal: 0, completedList: [], pendingList: [] }, action) => {
  switch (action.type) {
    case SET_PTP_PAYMENTS:
      return action.payload
    default:
      return state
  }
}

const instrumentStatus = (state = [], action) => {
  switch (action.type) {
    case SET_V2_PARTICIPANT_INSTRUMENTS:
    case SET_PARTICIPANT_INSTRUMENTS:
      return action.instruments
    default:
      return state
  }
}

const announcementStatus = (state = [], action) => {
  switch (action.type) {
    case SET_V2_PARTICIPANT_ANNOUNCEMENTS:
    case SET_PARTICIPANT_ANNOUNCEMENTS:
      return action.announcements
    default:
      return state
  }
}

const participantHistory = (state = [], action) => {
  switch (action.type) {
    case SET_PARTICIPANT_HISTORY:
      return action.history
    default:
      return state
  }
}

const diaryStatus = (state = [], action) => {
  switch (action.type) {
    case SET_V2_PARTICIPANT:
    case SET_PARTICIPANT: {
      return setParticipantDiaries(action.payload.ediaries)
    }
    default:
      return state
  }
}

export function checkedCohorts(state = {}, action) {
  let newState
  switch (action.type) {
    case INITIALIZE_CHECKED_COHORTS:
      newState = {}
      action.cohorts.forEach(cohortID => {
        newState[cohortID] = null
      })
      return newState
    case INITIALIZE_CREATE_PTP:
      return {}
    case TOGGLE_COHORT:
      newState = { ...state }
      if (action.id in newState) delete newState[action.id]
      else newState[action.id] = null
      return newState
    case CLEAR_COHORTS:
      return {}
    default:
      return state
  }
}

export const locales = (state = [], action) => {
  switch (action.type) {
    case SET_LOCALES:
      return action.locales
    default:
      return state
  }
}

export const instrumentTimeline = (state = [], action) => {
  switch (action.type) {
    case SET_V2_INSTRUMENT_TIMELINE:
      return action.payload
    case SET_INSTRUMENT_TIMELINE:
      return action.payload
    case RESET_INSTRUMENT_TIMELINE:
      return []
    default:
      return state
  }
}

export const instrumentSubmission = (
  state = { data: null, error: null, traceId: null },
  action,
) => {
  switch (action.type) {
    case SET_INSTRUMENT_SUBMISSION:
      return {
        data: action.payload,
        error: null,
        traceId: action.payload?.trace_id || null,
      }
    case SET_INSTRUMENT_SUBMISSION_ERROR:
      return {
        data: null,
        error: action.message,
        traceId: action.traceId || null,
      }
    case RESET_INSTRUMENT_SUBMISSION:
      return { data: null, error: null, traceId: null }
    default:
      return state
  }
}

export const diaryTimeline = (state = [], action) => {
  switch (action.type) {
    case SET_DIARY_TIMELINE:
      return action.payload
    case RESET_DIARY_TIMELINE:
      return []
    default:
      return state
  }
}

export const announcementTimeline = (state = [], action) => {
  switch (action.type) {
    case SET_V2_ANNOUNCEMENT_TIMELINE:
      return action.payload
    case SET_ANNOUNCEMENT_TIMELINE:
      return action.payload
    case RESET_ANNOUNCEMENT_TIMELINE:
      return []
    default:
      return state
  }
}

export const localesMap = (state = {}, action) => {
  switch (action.type) {
    case SET_LOCALES:
      return action.localesMap
    default:
      return state
  }
}

export const participantVisits = (state = [], action) => {
  switch (action.type) {
    case SET_PARTICIPANT_VISITS:
      return formatVisits(action.visits, action.isAdhocPage)
    case CLEAR_PARTICIPANT_VISITS:
    case RESET_PARTICIPANT:
      return []
    default:
      return state
  }
}

export const visitInstrumentsByVisitId = (state = {}, action) => {
  switch (action.type) {
    case SET_VISIT_INSTRUMENTS: {
      const newState = { ...state }
      newState[action.visitId] = action.payload
      return newState
    }
    case RESET_PARTICIPANT:
    case CLEAR_PARTICIPANT_VISITS:
      return {}
    default:
      return state
  }
}

export const visitInstrumentsLoadingByVisitId = (state = {}, action) => {
  switch (action.type) {
    case SET_VISIT_INSTRUMENTS_LOADING: {
      return { ...state, [action.visitId]: action.isLoading }
    }
    case RESET_PARTICIPANT:
    case CLEAR_PARTICIPANT_VISITS:
      return {}
    default:
      return state
  }
}

export const participantVisitCycles = (state = [], action) => {
  switch (action.type) {
    case SET_PARTICIPANT_VISIT_CYCLES:
      return action.visitCycles
    case CLEAR_PARTICIPANT_VISIT_CYCLES:
    case RESET_PARTICIPANT:
      return []
    default:
      return state
  }
}

export const visitTemplates = (state = [], action) => {
  switch (action.type) {
    case SET_VISIT_TEMPLATES:
      return Array.isArray(action.visits) ? action.visits : []
    default:
      return state
  }
}

export const files = (state = [], action) => {
  switch (action.type) {
    case SET_FILES:
      return parseFiles(action.files)
    default:
      return state
  }
}

export const isSubjectIdAvailable = (state = null, action) => {
  switch (action.type) {
    case SET_SUBJECT_ID_STATUS:
      return action.subjectIdStatus
    case RESET_SUBJECT_ID_STATUS:
      return null
    default:
      return state
  }
}

export default combineReducers({
  documents,
  participant,
  participantConsents,
  checkedCohorts,
  payments,
  instrumentStatus,
  announcementStatus,
  participantHistory,
  diaryStatus,
  locales,
  files,
  participantVisits,
  participantVisitCycles,
  visitTemplates,
  instrumentTimeline,
  instrumentSubmission,
  diaryTimeline,
  announcementTimeline,
  isSubjectIdAvailable,
  visitInstrumentsByVisitId,
  visitInstrumentsLoadingByVisitId,
})

export const actions = {
  closeModal: modalActions.closeModal,
  clearPtpVisits,
  clearPtpVisitCycles,
  downloadParticipantData,
  fetchFiles,
  fetchLocales,
  fetchParticipant,
  fetchParticipantVersionHistory,
  fetchPtpPayments,
  generateTempPassword,
  initializeCheckedCohorts,
  initializeEditPage,
  initializePage,
  markInstrumentComplete,
  openModal: modalActions.openModal,
  resetParticipant,
  saveToDatabase: openSaveModal,
  toggleCohort,
  clearCohorts,
  triggerPasswordReset,
  validateSubjectId,
  resetSubjectIdStatus,
  triggerResendInvite,
  updateField,
  ...documentsActions,
  submitCancelDeactivation,
  fetchParticipantVisits,
  fetchVisitTemplates,
  updatePreviousScheduleVisit,
  resetInstrumentTimeline,
  resetDiaryTimeline,
  fetchInstrumentTimeline,
  fetchInstrumentSubmission,
  resetInstrumentSubmission,
  fetchDiaryTimeline,
  resetAnnouncementTimeline,
  fetchAnnouncementTimeline,
  fetchVisitInstruments,
}

